import { SCRIBE_REGULATION_EVENTS } from 'applications/scribe/instrumentations/track';
import cx from 'classnames';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import canCreateDelivery from 'components/v2/anytime-booking/common/can-create-delivery';
import { PriceSurgeIcon } from 'components/v2/anytime-booking/common/price-surge-icon';
import { useServiceConfig } from 'components/v2/anytime-booking/common/service-configuration-field';
import { BookingMode } from 'constants/booking-constants';
import { getCountryCodeByCurrencyCode } from 'constants/currency-constants';
import { Icon } from 'grab-ui';
import { isAreaZoningError, isAreaZoningSoftBlock } from 'helper/error-codes';
import I18n from 'i18n-js';
import _every from 'lodash/every';
import _get from 'lodash/get';
import _some from 'lodash/some';
import PropTypes from 'prop-types';
import RecipientMSDIcon from 'public/icons/recipient-msd.svg';
import RecipientSingleIcon from 'public/icons/recipient-single.svg';
import SenderIcon from 'public/images/pick-up-2.svg';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  enableItemTypeFreeTextSelector,
  isItemTypeMandatorySelector,
} from 'selectors/app-selector';
import { deliveriesFromPropsSelector } from 'selectors/new-booking-selectors';
import {
  bulkUploadFetchingPromoSelector,
  bulkUploadRawDataSelector,
} from 'selectors/new-bulk-upload-selectors';
import {
  ErrorCircleIcon,
  WarningTriangleIcon,
} from 'views/v2/new-booking-page/quote-panel/warning-triangle-icon';

import ScribeDuck from 'ducks/scribe-duck';
import scribeNewBulkUploadDuck from '../../../../ducks/scribe-new-bulk-upload-duck';
import { PayAndBookContext } from '../common/context';
import pickUpKey from '../common/pick-up-key';
import BookDeliveriesButton from './book-deliveries-button';
import styles from './price-section.less';

const DeliveryPrice = ({ delivery }) => {
  const dispatch = useDispatch();
  const quote = _get(delivery, ['quote']);
  const currency = _get(quote, ['currency']);
  const currencyCode = _get(currency, ['code']);
  const discount = _get(quote, ['discount']);
  const discountName = _get(discount, ['name']);
  const discountAmount = _get(discount, ['discountAmount'], 0);
  const originalFare = _get(quote, ['originalFare', 'lowerBound'], 0);
  const dropOffs = _get(delivery, ['detail', 'dropOffs']);
  const dropOffName =
    dropOffs.length === 1
      ? _get(dropOffs[0], 'name')
      : I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_heading_amt_of_recipient_multistop_indicated',
          { amountOfRecipient: dropOffs.length },
        );
  const icon = dropOffs.length === 1 ? RecipientSingleIcon : RecipientMSDIcon;

  const countryCode = getCountryCodeByCurrencyCode(currencyCode);
  const { time } = useServiceConfig(delivery, countryCode);

  const paxExpressError = _get(delivery, 'paxExpressError');
  const errorCode = _get(paxExpressError, 'errorCode');
  const isEVError = isAreaZoningError(errorCode);
  const errorTitle = _get(paxExpressError, 'title');
  const errorMessage = _get(paxExpressError, 'message');

  useEffect(
    () => {
      if (isEVError) {
        dispatch(
          ScribeDuck.creators.scribeEVRegulationEvent({
            eventName:
              SCRIBE_REGULATION_EVENTS.EV_REGULATION_PRICE_BREAKDOWN_ERROR_VIEW,
            errorCode,
            title: errorMessage || errorTitle,
          }),
        );
      }
    },
    [isEVError, errorCode, errorTitle, errorMessage, dispatch],
  );

  return (
    <div className={styles.deliveryPrice}>
      <img className={styles.icon} src={icon} alt="" />
      <div className={styles.rows}>
        <div className={cx(styles.recipient, styles.row)}>
          <span>{dropOffName}</span>
          <span className={styles.fareContainer}>
            <span className={styles.right}>
              {formatCurrency({
                code: currencyCode,
                amount: calcPrice(originalFare, currency),
              })}
            </span>
            <PriceSurgeIcon priceSurgeInfo={quote} />
          </span>
        </div>
        <div className={cx(styles.eta, styles.row)}>
          {_get(time, 'pickUpTime')}
        </div>
        {discountAmount > 0 && (
          <div className={cx(styles.discount, styles.row)}>
            <span> {discountName} </span>
            <span className={styles.right}>
              {`-${formatCurrency({
                code: currencyCode,
                amount: calcPrice(discountAmount, currency),
              })}`}
            </span>
          </div>
        )}
        {isEVError && (
          <div className={cx(styles.warning, styles.row)}>
            {isAreaZoningSoftBlock(errorCode) ? (
              <WarningTriangleIcon />
            ) : (
              <ErrorCircleIcon />
            )}
            <span className={styles.warningText}>{errorTitle}</span>
          </div>
        )}
      </div>
    </div>
  );
};

DeliveryPrice.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

const PriceBreakdownWithPickUp = ({ deliveries }) => {
  const [collapsed, setCollapsed] = useState(false);
  const toggleBreakdown = useCallback(() => setCollapsed(!collapsed), [
    setCollapsed,
    collapsed,
  ]);

  return (
    <div className={styles.withPickUp}>
      <a className={styles.pickUpHeader} onClick={toggleBreakdown}>
        <img className={styles.icon} src={SenderIcon} alt="" />
        {_get(deliveries[0], 'detail.pickUp.name')} ({deliveries.length})
        <Icon type={collapsed ? 'down' : 'up'} className={styles.rightIcon} />
      </a>
      {!collapsed &&
        deliveries.map(delivery => (
          <DeliveryPrice key={delivery.id} delivery={delivery} />
        ))}
    </div>
  );
};

PriceBreakdownWithPickUp.propTypes = {
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
};

const PriceBreakdown = ({ deliveries, compactView }) => {
  const pickUps = useMemo(
    () => {
      const pickUps = {};
      deliveries.forEach(delivery => {
        const pickUp = _get(delivery, 'detail.pickUp');
        if (pickUp) {
          const key = pickUpKey(pickUp);
          if (!pickUps[key]) {
            pickUps[key] = [];
          }
          pickUps[key].push(delivery);
        }
      });
      return pickUps;
    },
    [deliveries],
  );

  if (Object.keys(pickUps).length === 1) {
    return (
      <div
        className={cx(styles.priceBreakdown, { [styles.compact]: compactView })}
      >
        {deliveries.map(delivery => (
          <DeliveryPrice key={delivery.id} delivery={delivery} />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cx(styles.priceBreakdown, { [styles.compact]: compactView })}
    >
      {Object.entries(pickUps).map(([pickUpKey, deliveries]) => (
        <PriceBreakdownWithPickUp key={pickUpKey} deliveries={deliveries} />
      ))}
    </div>
  );
};

PriceBreakdown.defaultProps = {
  compactView: false,
};

PriceBreakdown.propTypes = {
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  compactView: PropTypes.bool,
};

const TotalView = ({
  className,
  deliveryIDs,
  deliveries,
  fetchingPromo,
  rawData,
}) => {
  const dispatch = useDispatch();
  const {
    code,
    totalDiscount,
    totalOriginalFare,
    totalFinalFare,
    offerConfig,
  } = useContext(PayAndBookContext);

  const isItemTypeMandatory = useSelector(isItemTypeMandatorySelector);
  const enableItemTypeFreeText = useSelector(enableItemTypeFreeTextSelector);

  const deliveriesCount = I18n.t(
    'phrase.geWebBulkUpload.express_web_bulk_upload_heading_amt_of_deliveries_happypath',
    { amountOfDeliveries: deliveries.length },
  );

  const canCreateDeliveries = useMemo(
    () => {
      if (fetchingPromo) {
        return false;
      }
      return _every(deliveries, delivery =>
        canCreateDelivery(delivery, null, {
          isItemTypeMandatory,
          enableItemTypeFreeText,
        }),
      );
    },
    [fetchingPromo, deliveries, isItemTypeMandatory, enableItemTypeFreeText],
  );

  const onScribeBookDeliveries = useCallback(
    () => {
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadBookBook(
          offerConfig,
          totalFinalFare,
          code,
          deliveries.length,
          _some(
            rawData,
            row =>
              _get(row, 'receiverAdminArea') || _get(row, 'senderAdminArea'),
          ),
        ),
      );
    },
    [offerConfig, totalFinalFare, code, deliveries, rawData],
  );

  return (
    <div className={cx(styles.totalView, className)}>
      <div className={styles.totalInfo}>
        <div className={cx(styles.recipientsCount, styles.row)}>
          <span> {deliveriesCount} </span>
          <span className={styles.right}>
            {formatCurrency({
              code,
              amount: totalOriginalFare,
            })}
          </span>
        </div>
        {totalDiscount > 0 && (
          <div className={cx(styles.discount, styles.row)}>
            <span>
              {I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_heading_offers_happypath',
              )}
            </span>
            <span className={styles.right}>
              {`-${formatCurrency({
                code,
                amount: totalDiscount,
              })}`}
            </span>
          </div>
        )}
        <div className={cx(styles.totalPrice, styles.row)}>
          <span>
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_body_total_happypath',
            )}
          </span>
          <span className={styles.right}>
            {formatCurrency({
              code,
              amount: totalFinalFare,
            })}
          </span>
        </div>
      </div>
      <BookDeliveriesButton
        disabled={!canCreateDeliveries}
        buttonText={I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_button_book_deliveries_happypath',
          { amountOfDeliveries: deliveries.length },
        )}
        deliveryIDs={deliveryIDs}
        bookingMode={BookingMode.Bulk}
        beforeFn={onScribeBookDeliveries}
      />
    </div>
  );
};

TotalView.defaultProps = {
  className: undefined,
};

TotalView.propTypes = {
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  fetchingPromo: PropTypes.bool.isRequired,
  className: PropTypes.string,
  rawData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const PriceSection = ({ deliveryIDs, deliveries, fetchingPromo, rawData }) => {
  const compactView = deliveries.length > 10;
  const [collapsed, setCollapsed] = useState(false);
  const toggleBreakdown = useCallback(() => setCollapsed(!collapsed), [
    setCollapsed,
    collapsed,
  ]);

  const floatingRef = useRef();
  const [floatingHeight, setFloatingHeight] = useState(0);
  useLayoutEffect(
    () => {
      if (floatingRef.current) {
        setFloatingHeight(floatingRef.current.offsetHeight);
      }
    },
    [floatingRef.current, collapsed, setFloatingHeight],
  );

  if (compactView) {
    if (collapsed) {
      return (
        <div className={styles.priceSection}>
          <a className={styles.priceBreakdownButton} onClick={toggleBreakdown}>
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_heading_price_breakdown_happypath',
            )}
            <Icon type={'down'} className={styles.icon} />
          </a>
          <TotalView
            key={'totalView'}
            fetchingPromo={fetchingPromo}
            deliveryIDs={deliveryIDs}
            deliveries={deliveries}
            rawData={rawData}
          />
        </div>
      );
    }

    return (
      <div
        className={styles.priceSection}
        style={{ paddingBottom: floatingHeight + 24 }}
      >
        <a className={styles.priceBreakdownButton} onClick={toggleBreakdown}>
          {I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_heading_price_breakdown_happypath',
          )}
          <Icon type={'up'} className={styles.icon} />
        </a>
        <PriceBreakdown deliveries={deliveries} compactView />
        <div className={styles.floatingTotalView} ref={floatingRef}>
          <TotalView
            key={'totalView'}
            fetchingPromo={fetchingPromo}
            deliveryIDs={deliveryIDs}
            deliveries={deliveries}
            rawData={rawData}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.priceSection}>
      <span className={styles.priceBreakdownButton}>
        {I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_heading_price_breakdown_happypath',
        )}
      </span>
      <PriceBreakdown deliveries={deliveries} />
      <TotalView
        className={styles.nonCompact}
        deliveryIDs={deliveryIDs}
        deliveries={deliveries}
        fetchingPromo={fetchingPromo}
        rawData={rawData}
      />
    </div>
  );
};

PriceSection.defaultProps = {
  className: undefined,
};

PriceSection.propTypes = {
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  fetchingPromo: PropTypes.bool.isRequired,
  rawData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
  fetchingPromo: bulkUploadFetchingPromoSelector(state),
  rawData: bulkUploadRawDataSelector(state),
}))(PriceSection);
